import useLocalStorage from '@rehooks/local-storage';
import React, { useCallback, useContext, useEffect } from 'react';
import { useHistory } from 'react-router';

import { Collections } from 'Collections';
import { analytics } from 'FirebaseConfigs';
import { FirebaseUtils } from 'firebaseUtils/FirebaseUtils';
import { useUrlParam } from 'hooks/useUrlParam';
import { LocalStorageKeys } from 'LocalStorageKeys';
import { AnalyticsEvent } from 'models/AnalyticsEvent';
import { DbDoc, WithRef } from 'models/FbDoc';
import { Org } from 'models/Org';
import { Paths } from 'Paths';

import { useAppUser } from './AppUserProvider';
import { useOrgUser } from './OrgUserProvider';
import { RunAfterTimer } from './RunAfterTimer';
import { Snacbars } from './Snacbars';

type Props = {
    children: React.ReactNode
}

const ORG_CONTEXT = React.createContext<[org: WithRef<DbDoc<Org>> | null, loading: boolean]>([null, true])

export function useOrg() {
    return useContext(ORG_CONTEXT)
}

export function useOrgIdLocalStorage(): [orgId: string | null, setOrgId: (newOrgId: string | null) => void] {
    const [orgId, setOrgId] = useLocalStorage<string | null>(LocalStorageKeys.ORG_ID, null)
    return [orgId, setOrgId]
}

export function useUnselectOrg(): [unselectOrg: (contextEvent: AnalyticsEvent) => void, loading: boolean] {
    const history = useHistory()
    const [appUser, loadingAppUser] = useAppUser()
    const [orgUser, loadingOrgUser] = useOrgUser()
    const [orgId, setOrgId] = useOrgIdLocalStorage()
    return [
        useCallback((contextEvent: AnalyticsEvent) => {
            analytics.logEvent(AnalyticsEvent.UNSELECTED_ORG, {
                orgId,
                appUserId: appUser?.data.id,
                orgUserId: orgUser?.data.id,
                contextEvent,
            })
            setOrgId(null)
            history.push(Paths.SELECT_ORG)
        }, [orgUser, appUser, orgId, setOrgId, history]),
        loadingOrgUser || loadingAppUser,
    ]
}

export function OrgProvider({ children }: Props) {
    const targetOrgId = useUrlParam('orgId')
    const [appUser, loadingAppUser] = useAppUser();
    const [orgId, setOrgId] = useOrgIdLocalStorage()
    const [unselectOrg] = useUnselectOrg()
    const [org, loadingOrg, error] = FirebaseUtils.useDocumentData<Org>(
        true,
        Collections.ORGS,
        orgId,
        AnalyticsEvent.FETCH_ORG_ERROR
    )

    const loading = loadingAppUser || loadingOrg

    useEffect(() => {
        if (targetOrgId) {
            //Prevent this from triggering  unnecessarily
            setOrgId(targetOrgId)
        }
    }, [targetOrgId, setOrgId])

    const timer =
        <RunAfterTimer
            time={5000}
            check={!loading && !org && !!orgId}
            run={useCallback(() => {
                analytics.logEvent(AnalyticsEvent.MISSING_ORG, {
                    appUserId: appUser?.data.id,
                    storedOrgId: orgId,
                    orgId: org?.data.id,
                })
                unselectOrg(AnalyticsEvent.MISSING_ORG)
            }, [appUser, org, orgId, unselectOrg])} />

    return (
        <ORG_CONTEXT.Provider value={[org || null, loading]}>
            <Snacbars error={error?.message} />
            {timer}
            {children}
        </ORG_CONTEXT.Provider>
    )
}
